import { GeneratedStack, GeneratedNode, GeneratedEdge, GeneratedGroup } from '../types';
import { validateAndNormalizeIcon, getFallbackIconForCategory } from './iconValidation';

/**
 * Result of validation with possible corrections
 */
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  correctedStack?: GeneratedStack;
}

/**
 * Validates and corrects a generated stack from AI
 */
export function validateAndCorrectStack(stack: GeneratedStack): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const correctedStack: GeneratedStack = {
    nodes: [],
    edges: [],
    groups: stack.groups || [],
    stickyNotes: stack.stickyNotes || [],
    textNodes: stack.textNodes || [],
    notes: stack.notes,
  };

  // Track all node IDs for edge validation
  const nodeIds = new Set<string>();

  // Validate and correct nodes
  for (const node of stack.nodes) {
    const nodeResult = validateAndCorrectNode(node);
    
    if (nodeResult.errors.length > 0) {
      errors.push(...nodeResult.errors.map(e => `Node '${node.id}': ${e}`));
    }
    
    if (nodeResult.warnings.length > 0) {
      warnings.push(...nodeResult.warnings.map(w => `Node '${node.id}': ${w}`));
    }
    
    if (nodeResult.correctedNode) {
      correctedStack.nodes.push(nodeResult.correctedNode);
      nodeIds.add(nodeResult.correctedNode.id);
    }
  }

  // Validate and correct edges
  for (const edge of stack.edges) {
    const edgeResult = validateAndCorrectEdge(edge, nodeIds);
    
    if (edgeResult.errors.length > 0) {
      errors.push(...edgeResult.errors.map(e => `Edge '${edge.source}-${edge.target}': ${e}`));
    }
    
    if (edgeResult.warnings.length > 0) {
      warnings.push(...edgeResult.warnings.map(w => `Edge '${edge.source}-${edge.target}': ${w}`));
    }
    
    if (edgeResult.correctedEdge) {
      correctedStack.edges.push(edgeResult.correctedEdge);
    }
  }

  // Validate groups
  if (stack.groups) {
    for (const group of stack.groups) {
      const groupResult = validateGroup(group, nodeIds);
      
      if (groupResult.errors.length > 0) {
        errors.push(...groupResult.errors.map(e => `Group '${group.id}': ${e}`));
      }
      
      if (groupResult.warnings.length > 0) {
        warnings.push(...groupResult.warnings.map(w => `Group '${group.id}': ${w}`));
      }
    }
  }

  // Check for disconnected nodes (warning only)
  const connectedNodeIds = new Set<string>();
  correctedStack.edges.forEach(edge => {
    connectedNodeIds.add(edge.source);
    connectedNodeIds.add(edge.target);
  });
  
  const disconnectedNodes = correctedStack.nodes.filter(n => !connectedNodeIds.has(n.id));
  if (disconnectedNodes.length > 0 && disconnectedNodes.length < correctedStack.nodes.length) {
    warnings.push(`${disconnectedNodes.length} node(s) are not connected to any other component`);
  }

  const isValid = errors.length === 0;
  
  return {
    isValid,
    errors,
    warnings,
    correctedStack: isValid ? stack : correctedStack,
  };
}

/**
 * Validate and correct a single node
 */
function validateAndCorrectNode(node: GeneratedNode): {
  errors: string[];
  warnings: string[];
  correctedNode?: GeneratedNode;
} {
  const errors: string[] = [];
  const warnings: string[] = [];
  const correctedNode = { ...node };

  // Validate ID
  if (!node.id || node.id.trim() === '') {
    errors.push('Node ID is required');
    return { errors, warnings };
  }

  // Validate label
  if (!node.label || node.label.trim() === '') {
    errors.push('Node label is required');
    correctedNode.label = 'Unknown Component';
  }

  // Validate and correct icon
  const iconResult = validateAndNormalizeIcon(node.icon);
  if (!iconResult.isValid) {
    warnings.push(iconResult.error || 'Invalid icon');
    if (iconResult.normalizedIcon) {
      correctedNode.icon = iconResult.normalizedIcon;
    } else if (iconResult.fallbackIcon) {
      correctedNode.icon = iconResult.fallbackIcon;
      warnings.push(`Using fallback icon for '${node.label}'`);
    }
  }

  // Validate description. A missing one is fine — the node just renders without
  // it — so warn but don't invent placeholder copy.
  if (!node.description || node.description.trim() === '') {
    warnings.push('Node description is missing');
  }

  // Validate category
  if (node.category) {
    const validCategories = ['frontend', 'backend', 'database', 'infrastructure', 'devops', 'security', 'monitoring', 'storage', 'network', 'cloud'];
    if (!validCategories.includes(node.category)) {
      warnings.push(`Invalid category '${node.category}', will be ignored`);
      delete correctedNode.category;
    }
  }

  // Validate importance
  if (node.importance && (node.importance < 1 || node.importance > 5)) {
    warnings.push(`Invalid importance level ${node.importance}, using default 3`);
    correctedNode.importance = 3;
  }

  // Validate group reference
  if (node.groupId && node.groupId.trim() !== '') {
    // Will be validated at the group level
  }

  return { errors, warnings, correctedNode };
}

/**
 * Validate and correct a single edge
 */
function validateAndCorrectEdge(edge: GeneratedEdge, nodeIds: Set<string>): {
  errors: string[];
  warnings: string[];
  correctedEdge?: GeneratedEdge;
} {
  const errors: string[] = [];
  const warnings: string[] = [];
  const correctedEdge = { ...edge };

  // Validate source and target exist
  if (!nodeIds.has(edge.source)) {
    errors.push(`Source node '${edge.source}' does not exist`);
  }

  if (!nodeIds.has(edge.target)) {
    errors.push(`Target node '${edge.target}' does not exist`);
  }

  // Handles are derived from final layout geometry (services/layoutService.ts
  // deriveHandles) — never defaulted here. Stale/invalid values from the model
  // or parser are simply dropped so derivation fills them in.
  const validHandles = ['top', 'bottom', 'left', 'right'];
  if (edge.sourceHandle && !validHandles.includes(edge.sourceHandle)) {
    delete correctedEdge.sourceHandle;
  }
  if (edge.targetHandle && !validHandles.includes(edge.targetHandle)) {
    delete correctedEdge.targetHandle;
  }

  // Validate edge type
  if (edge.type) {
    const validTypes = ['data', 'api', 'event', 'dependency', 'control'];
    if (!validTypes.includes(edge.type)) {
      warnings.push(`Invalid edge type '${edge.type}', will be ignored`);
      delete correctedEdge.type;
    }
  }

  // Check for self-loops
  if (edge.source === edge.target) {
    warnings.push('Edge connects a node to itself');
  }

  return { errors, warnings, correctedEdge };
}

/**
 * Validate a group
 */
function validateGroup(group: GeneratedGroup, nodeIds: Set<string>): {
  errors: string[];
  warnings: string[];
} {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Validate group ID
  if (!group.id || group.id.trim() === '') {
    errors.push('Group ID is required');
  }

  // Validate group label
  if (!group.label || group.label.trim() === '') {
    errors.push('Group label is required');
  }

  // Validate node references
  const missingNodes = group.nodeIds.filter(id => !nodeIds.has(id));
  if (missingNodes.length > 0) {
    warnings.push(`Group references non-existent nodes: ${missingNodes.join(', ')}`);
  }

  // Check for empty groups
  if (group.nodeIds.length === 0) {
    warnings.push('Group has no members');
  }

  return { errors, warnings };
}

/**
 * Attempt to fix common issues in a generated stack
 */
export function attemptAutoFix(stack: GeneratedStack): GeneratedStack {
  const fixed = { ...stack };

  // Fix missing icons
  fixed.nodes = fixed.nodes.map(node => {
    if (!node.icon) {
      return {
        ...node,
        icon: getFallbackIconForCategory(node.category),
      };
    }
    return node;
  });

  // Fix missing importance levels
  fixed.nodes = fixed.nodes.map(node => ({
    ...node,
    importance: node.importance || 3,
  }));

  return fixed;
}
